
import throttle from "lodash/throttle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../../components";
import "./_scrollable-tabs.scss";


/**
 * ScrollableTabs provides a horizontally scrollable tab container with left/right navigation.
 * @param enableScroll Enable scroll behavior
 * @param children Tab elements
 * @param numberOfTabs Number of tabs
 * @param additionalMarginPerTab Extra margin per tab (px)
 */
export interface ScrollableTabsProps {
    enableScroll: boolean;
    children: React.ReactNode;
    numberOfTabs: number;
    additionalMarginPerTab?: number;
}


const ScrollableTabs: React.FC<ScrollableTabsProps> = React.memo(({
    enableScroll = false,
    children,
    numberOfTabs,
    additionalMarginPerTab = 0
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const prevNumberOfTabsRef = useRef<number>(numberOfTabs);
    const pendingScrollToEndRef = useRef(false);
    const [tabInfo, setTabInfo] = useState<{
        left: number;
        right: number;
        visible: number;
        tabSizes: number[];
    }>({
        left: 0,
        visible: 0,
        right: 0,
        tabSizes: []
    });


    useEffect(() => {
        if (tabInfo.tabSizes.length === 0) updateArrows();
    }, [tabInfo.tabSizes]);


    useEffect(() => {
        if (!enableScroll || prevNumberOfTabsRef.current >= numberOfTabs) return;
        resetActiveTabs();
        const handleResize = throttle(() => {
            resetActiveTabs();
            updateArrows();
        }, 500);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [enableScroll, numberOfTabs]);


    useEffect(() => {
        if (numberOfTabs > prevNumberOfTabsRef.current) {
            pendingScrollToEndRef.current = true;
        }
        if (numberOfTabs < prevNumberOfTabsRef.current && tabInfo.tabSizes.length > 0) {
            const lastVisibleTabIdx = tabInfo.left + tabInfo.visible - 1;
            if (lastVisibleTabIdx >= numberOfTabs && tabInfo.visible <= 1) {
                pendingScrollToEndRef.current = true;
            }
            setTimeout(() => {
                updateArrows();
            }, 0);
        }
        prevNumberOfTabsRef.current = numberOfTabs;
    }, [numberOfTabs, tabInfo.left, tabInfo.visible, tabInfo.tabSizes.length]);



    const resetActiveTabs = useCallback(() => {
        setTabInfo((prev) => ({ ...prev, tabSizes: [] }));
    }, []);


    const updateArrows = useCallback(() => {
        const inner = containerRef.current;
        const container = inner?.parentElement || inner;
        if (!inner || !container) return;

        const allTabWidths: number[] = Array.from(inner.children).map(
            (el) => (el as HTMLElement).offsetWidth
        );

        let left = tabInfo.left;
        let newVisible = 0;
        if (pendingScrollToEndRef.current && allTabWidths.length > 0) {
            let leftIdx = allTabWidths.length - 1;
            let widthSum = allTabWidths[leftIdx] + additionalMarginPerTab;
            while (
                leftIdx > 0 &&
                widthSum + allTabWidths[leftIdx - 1] + additionalMarginPerTab <= container.clientWidth
            ) {
                leftIdx--;
                widthSum += allTabWidths[leftIdx] + additionalMarginPerTab;
            }
            left = leftIdx;
            pendingScrollToEndRef.current = false;
        }

        let totalWidth = 0;
        for (let i = left; i < allTabWidths.length; i++) {
            totalWidth += allTabWidths[i] + additionalMarginPerTab;
            if (totalWidth > container.clientWidth) break;
            newVisible++;
        }
        setTabInfo((prev) => ({
            ...prev,
            left,
            visible: newVisible,
            right: allTabWidths.length - (left + newVisible),
            tabSizes: allTabWidths,
        }));
    }, [additionalMarginPerTab, tabInfo.left]);

    const shift = useCallback((direction: "left" | "right") => {
        if (!containerRef.current) return;
        let newLeft = tabInfo.left;
        if (direction === "left") {
            newLeft = Math.max(0, tabInfo.left - 1);
        } else if (tabInfo.left + tabInfo.visible < tabInfo.tabSizes.length) {
            newLeft = tabInfo.left + 1;
        }
        setTabInfo((prevTab) => ({
            ...prevTab,
            left: newLeft,
        }));
    }, [tabInfo.left, tabInfo.visible, tabInfo.tabSizes.length]);


    useEffect(() => {
        if (tabInfo.tabSizes.length > 0) {
            updateArrows();
        }
    }, [tabInfo.left, updateArrows]);


    if (enableScroll) {
        const offset =
            tabInfo.tabSizes.slice(0, tabInfo.left).reduce((acc, w) => acc + w + additionalMarginPerTab, 0);

        return (
            <div className={"scroll-tab"} data-testid={"scroll-tab-container"}>
                {tabInfo.left > 0 && (
                    <Button
                        icon="left"
                        onClick={() => shift("left")}
                        size="medium"
                        className={"scroll-arrows left"}
                        iconName="chevron_left"
                    />
                )}
                <div
                    ref={containerRef}
                    className="scroll-tab-inner"
                    style={{
                        display: "flex",
                        transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
                        transform: `translateX(-${offset}px)`,
                        width: "max-content"
                    }}
                >
                    {children}
                </div>
                {(tabInfo.right > 0) && (
                    <Button
                        icon="right"
                        onClick={() => shift("right")}
                        size="medium"
                        className={"scroll-arrows right"}
                        iconName="chevron_right"
                    />
                )}
            </div>
        );
    }
    return <>{children}</>;
});

export default ScrollableTabs;